import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/common/ui";
import { seriesColor } from "@/lib/chart";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Receipt, Pencil } from "lucide-react";

const PAY_TONE = {
  "Paid in Full": "border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))]",
  "Partially Paid": "border-[hsl(var(--warning)/0.4)] text-[hsl(var(--warning))]",
  "Not Paid": "border-[hsl(var(--danger)/0.4)] text-[hsl(var(--danger))]",
};

/**
 * SpendListSheet — reusable right-side drawer that opens when a chart segment
 * is clicked, listing the underlying spends grouped by vertical (default) or
 * by vendor (`drill.groupBy = "vendor"`, used by the platform chart).
 *
 * `drill` = { title, subtitle, groupBy?, filters: { vis_perf?, platform?, vendor?, vertical?, project?, bucket? } }
 */
export function SpendListSheet({ drill, period = "FY", projects = [], onClose }) {
  const { money } = useCurrency();
  const { isFull } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const projKey = (projects || []).join(",");
  const groupBy = drill?.groupBy === "vendor" ? "vendor" : "vertical";

  useEffect(() => {
    if (!drill) return;
    setData(null); setQ("");
    const params = { period, ...(drill.filters || {}) };
    if (projKey) params.projects = projKey;
    api.spendList(params).then(setData).catch(() => setData({ total: 0, rows: 0, by_vertical: [], items: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drill, period, projKey]);

  const filtered = useMemo(() => {
    const items = data?.items || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((r) =>
      [r.activity, r.vendor, r.platform, r.vertical, r.project, r.po_number]
        .some((f) => (f || "").toLowerCase().includes(needle)));
  }, [data, q]);

  // group items by vertical (default) or vendor
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const key = (groupBy === "vendor" ? r.vendor : r.vertical) || "Other";
      if (!map.has(key)) map.set(key, { name: key, spend: 0, items: [] });
      const g = map.get(key);
      g.spend += r.amount || 0;
      g.items.push(r);
    });
    return Array.from(map.values()).sort((a, b) => b.spend - a.spend);
  }, [filtered, groupBy]);

  const chips = useMemo(() => {
    if (groupBy !== "vendor") return data?.by_vertical || [];
    const total = grouped.reduce((s, g) => s + g.spend, 0);
    return grouped.slice(0, 6).map((g) => ({ name: g.name, spend: g.spend, share: total ? g.spend / total : 0 }));
  }, [data, grouped, groupBy]);

  return (
    <Sheet open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl" data-testid="spend-list-sheet">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="flex items-center gap-2 text-base">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(var(--primary)/0.12)] text-primary">
              <Receipt className="h-4 w-4" />
            </span>
            <span data-testid="spend-list-title">{drill?.title}</span>
          </SheetTitle>
          <SheetDescription data-testid="spend-list-subtitle">
            {data ? `${money(data.total)} across ${data.rows} spend line${data.rows === 1 ? "" : "s"}${drill?.subtitle ? ` \u00b7 ${drill.subtitle}` : ""}` : "Loading\u2026"}
          </SheetDescription>
        </SheetHeader>

        {!data ? (
          <div className="space-y-2 p-5">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-md" />)}</div>
        ) : (data.items || []).length === 0 ? (
          <div className="p-5"><EmptyState title="No spends" message="No spend lines in this segment." /></div>
        ) : (
          <>
            {/* group summary chips */}
            <div className="flex flex-wrap gap-1.5 border-b px-5 py-3" data-testid="spend-list-category-chips">
              {chips.map((g, i) => (
                <span key={g.name} className="inline-flex items-center gap-1.5 rounded-full border bg-[hsl(var(--surface-2))] px-2.5 py-1 text-[11px]">
                  <span className="h-2 w-2 rounded-full" style={{ background: seriesColor(i) }} />
                  <span className="font-medium">{g.name}</span>
                  <span className="tabular-nums text-muted-foreground">{money(g.spend)} · {Math.round((g.share || 0) * 100)}%</span>
                </span>
              ))}
            </div>
            <div className="border-b px-5 py-2.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by activity, vendor, PO…"
                  className="h-8 pl-8 text-sm" data-testid="spend-list-search" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3" data-testid="spend-list-body">
              {grouped.length === 0 ? (
                <EmptyState title="No matches" message={`Nothing matches "${q}".`} />
              ) : grouped.map((g, gi) => (
                <div key={g.name} className="mb-4 last:mb-1" data-testid={`spend-list-group-${g.name.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                  <div className="mb-1.5 flex items-center justify-between border-b pb-1.5">
                    <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: seriesColor(gi) }} />
                      {g.name} <span className="font-normal text-muted-foreground">· {g.items.length}</span>
                    </span>
                    <span className="num text-xs font-semibold tabular-nums">{money(g.spend)}</span>
                  </div>
                  <div className="space-y-0.5">
                    {g.items.map((r, i) => (
                      <div key={r.id || i} className="group flex items-start justify-between gap-3 rounded-md px-1.5 py-1.5 hover:bg-[hsl(var(--surface-2))]" data-testid="spend-list-item">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium" title={r.activity}>{r.activity || "\u2014"}</p>
                          <p className="truncate text-[11px] text-muted-foreground">
                            {r.month} · {(groupBy === "vendor" ? r.vertical : r.vendor) || "\u2014"}{r.platform ? ` · ${r.platform}` : ""}{r.project ? ` · ${r.project}` : ""}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-start gap-1">
                          <div className="text-right">
                            <p className="num text-sm font-semibold tabular-nums">{money(r.amount)}</p>
                            {r.payment_status && (
                              <Badge variant="outline" className={`mt-0.5 text-[9px] ${PAY_TONE[r.payment_status] || "text-muted-foreground"}`}>
                                {r.payment_status}
                              </Badge>
                            )}
                          </div>
                          {isFull && r.id && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                              title="Edit this spend" aria-label="Edit this spend"
                              onClick={() => navigate(`/spends?edit=${encodeURIComponent(r.id)}`)}
                              data-testid="spend-list-item-edit">
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {data.truncated && <p className="pb-2 text-center text-[11px] text-muted-foreground">Showing the top {data.items.length} lines by amount.</p>}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
